import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const CountdownOverlay = ({ seconds, onComplete, soundEnabled = true }) => {
  const { t } = useLanguage();
  const [count, setCount] = useState(seconds);
  const audioContextRef = useRef(null);

  // Create beep sound using Web Audio API
  const playBeep = useCallback((isLast = false) => {
    if (!soundEnabled) return;
    
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      // Higher pitch for last beep (go!)
      oscillator.frequency.value = isLast ? 880 : 440;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + (isLast ? 0.3 : 0.15));
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + (isLast ? 0.3 : 0.15));
    } catch (e) {
      // Audio not supported or blocked
    }
  }, [soundEnabled]);

  useEffect(() => {
    // Play beep on mount and each count change
    if (count > 0) {
      playBeep(false);
    } else {
      playBeep(true); // Final "go" beep
    }
  }, [count, playBeep]);

  useEffect(() => {
    if (count <= 0) {
      // Small delay before completing to show "GO!"
      const completeTimer = setTimeout(() => {
        onComplete();
      }, 500);
      return () => clearTimeout(completeTimer);
    }

    const timer = setTimeout(() => {
      setCount(count - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [count, onComplete]);

  // Cleanup audio context
  useEffect(() => {
    return () => {
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  return (
    <div className="countdown-overlay" data-testid="countdown-overlay">
      <div className="countdown-number" key={count}>
        {count > 0 ? count : 'GO!'}
      </div>
      <div className="countdown-text">
        {count > 0 ? (t('wheel.getReady') || 'Get Ready!') : ''}
      </div>
    </div>
  );
};

export default CountdownOverlay;
